import React, { useState, useCallback, useEffect } from 'react';
import { Upload, Image, X, FolderOpen, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

const MediaSelector = ({ 
  value, 
  onChange, 
  label = 'Image',
  placeholder = 'Select or upload an image',
  showPreview = true,
  className = ''
}) => {
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const [mediaRes, foldersRes] = await Promise.all([
        api.get('/media'),
        api.get('/media/folders')
      ]);
      setMedia(mediaRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showMediaBrowser) {
      fetchMedia();
    }
  }, [showMediaBrowser]);

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const file = files[0]; // Single file upload
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success('Image uploaded successfully');
      onChange(response.data.url);
      setShowMediaBrowser(false);
      fetchMedia();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files);
    }
  }, [handleUpload]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleSelectImage = (url) => {
    onChange(url);
    setShowMediaBrowser(false);
  };

  const clearImage = () => {
    onChange('');
  };

  // Filter media for current folder and search
  const filteredMedia = media.filter(item => {
    const inFolder = currentFolderId ? item.folder_id === currentFolderId : !item.folder_id;
    const matchesSearch = !searchTerm || 
      item.filename?.toLowerCase().includes(searchTerm.toLowerCase());
    const isImage = item.type?.startsWith('image/') || 
      item.filename?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
    return inFolder && matchesSearch && isImage;
  });

  // Get subfolders for current folder
  const currentFolders = folders.filter(f => 
    currentFolderId ? f.parent_id === currentFolderId : !f.parent_id
  );

  // Get breadcrumb path
  const getBreadcrumbs = () => {
    const breadcrumbs = [{ id: null, name: 'All Media' }];
    if (currentFolderId) {
      let folder = folders.find(f => f.folder_id === currentFolderId);
      const path = [];
      while (folder) {
        path.unshift({ id: folder.folder_id, name: folder.name });
        folder = folders.find(f => f.folder_id === folder.parent_id);
      }
      breadcrumbs.push(...path);
    }
    return breadcrumbs;
  };

  return (
    <div className={className}>
      {/* Drop Zone / Preview */}
      <div
        className={`relative border-2 border-dashed rounded-lg transition-colors ${
          dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-slate-400'
        } ${value ? 'p-2' : 'p-6'}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {value && showPreview ? (
          <div className="relative">
            <img 
              src={value} 
              alt="Selected" 
              className="max-h-40 rounded mx-auto object-contain"
            />
            <button
              type="button"
              onClick={clearImage}
              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="text-center">
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="animate-spin text-indigo-500 mb-2" size={32} />
                <p className="text-sm text-slate-500">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="mx-auto mb-2 text-slate-400" size={32} />
                <p className="text-sm text-slate-600 mb-1">
                  Drag & drop an image here
                </p>
                <p className="text-xs text-slate-400 mb-3">or</p>
                <div className="flex gap-2 justify-center">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowMediaBrowser(true)}
                  >
                    <FolderOpen size={16} className="mr-1" />
                    Browse Media
                  </Button>
                  <label>
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        <Upload size={16} className="mr-1" />
                        Upload
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files)}
                    />
                  </label>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Manual URL Input */}
      {value && (
        <div className="mt-2 flex gap-2">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or enter image URL"
            className="text-xs"
          />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowMediaBrowser(true)}
          >
            <FolderOpen size={14} />
          </Button>
        </div>
      )}

      {/* Media Browser Modal */}
      <Dialog open={showMediaBrowser} onOpenChange={setShowMediaBrowser}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image size={20} />
              Select Image from Media Library
            </DialogTitle>
          </DialogHeader>

          {/* Search and Upload */}
          <div className="flex gap-2 py-2">
            <Input
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <label>
              <Button type="button" variant="outline" asChild disabled={uploading}>
                <span>
                  {uploading ? (
                    <Loader2 className="animate-spin mr-1" size={16} />
                  ) : (
                    <Upload size={16} className="mr-1" />
                  )}
                  Upload New
                </span>
              </Button>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
              />
            </label>
          </div>

          {/* Breadcrumbs */}
          <div className="flex items-center gap-1 text-sm py-2 border-b">
            {getBreadcrumbs().map((crumb, idx) => (
              <React.Fragment key={crumb.id || 'root'}>
                {idx > 0 && <span className="text-slate-400">/</span>}
                <button
                  onClick={() => setCurrentFolderId(crumb.id)}
                  className={`hover:text-indigo-600 ${
                    currentFolderId === crumb.id ? 'text-indigo-600 font-medium' : 'text-slate-600'
                  }`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Media Grid */}
          <div className="flex-1 overflow-y-auto py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-indigo-500" size={32} />
              </div>
            ) : (
              <>
                {/* Folders */}
                {currentFolders.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-2">Folders</p>
                    <div className="grid grid-cols-4 gap-2">
                      {currentFolders.map(folder => (
                        <button
                          key={folder.folder_id}
                          onClick={() => setCurrentFolderId(folder.folder_id)}
                          className="flex items-center gap-2 p-3 border rounded-lg hover:bg-slate-50 text-left"
                        >
                          <FolderOpen size={20} className="text-amber-500" />
                          <span className="text-sm truncate">{folder.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Images */}
                {filteredMedia.length > 0 ? (
                  <div className="grid grid-cols-4 gap-3">
                    {filteredMedia.map(item => (
                      <button
                        key={item.media_id}
                        onClick={() => handleSelectImage(item.url)}
                        className={`relative aspect-square border-2 rounded-lg overflow-hidden hover:border-indigo-500 transition-colors ${
                          value === item.url ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200'
                        }`}
                      >
                        <img
                          src={item.url}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                        />
                        {value === item.url && (
                          <div className="absolute top-1 right-1 bg-indigo-500 text-white rounded-full p-1">
                            <Check size={12} />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                          {item.filename}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Image size={48} className="mx-auto mb-2 opacity-30" />
                    <p>No images found</p>
                    <p className="text-xs mt-1">Upload an image or try a different folder</p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaSelector;
